/**
 * Fix Clean Week Recognition Data
 *
 * Fixes two issues with existing Clean Week recognitions:
 * 1. Student names containing "undefined" (missing lastName)
 * 2. Recognitions awarded to graduated students (should be deleted)
 *
 * USAGE:
 *   await window.fixCleanWeekRecognitions()                    // Dry run (preview)
 *   await window.fixCleanWeekRecognitions({ execute: true })   // Apply fixes
 */

import { db, auth } from '@/firebase';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';

/**
 * Main fix function
 */
async function fixCleanWeekRecognitions(options = {}) {
  const { execute = false } = options;

  console.log('='.repeat(60));
  console.log('FIX CLEAN WEEK RECOGNITION DATA');
  console.log(execute ? '*** EXECUTE MODE ***' : '*** PREVIEW MODE ***');
  console.log('='.repeat(60));

  const user = auth.currentUser;
  if (!user) {
    console.error('Not logged in.');
    return { success: false, error: 'Not authenticated' };
  }

  const schoolId = localStorage.getItem('currentSchoolId');
  if (!schoolId) {
    console.error('No school selected.');
    return { success: false, error: 'No school selected' };
  }

  console.log(`School: ${schoolId}`);
  console.log(`User: ${user.displayName || user.email}\n`);

  // Fetch all students (including graduated to identify them)
  console.log('Fetching all students...');
  const studentsRef = collection(db, 'schools', schoolId, 'students');
  const studentsSnapshot = await getDocs(studentsRef);

  const studentsMap = new Map();
  const graduatedStudentIds = new Set();

  studentsSnapshot.forEach(doc => {
    const data = doc.data();
    studentsMap.set(doc.id, { id: doc.id, ...data });
    if (data.graduated === true) {
      graduatedStudentIds.add(doc.id);
    }
  });

  console.log(`Found ${studentsMap.size} total students`);
  console.log(`  - ${graduatedStudentIds.size} graduated students\n`);

  // Fetch all recognitions
  console.log('Fetching recognitions...');
  const recognitionsRef = collection(db, 'schools', schoolId, 'recognitions');
  const recognitionsSnapshot = await getDocs(recognitionsRef);

  const recognitions = [];
  recognitionsSnapshot.forEach(doc => {
    recognitions.push({ id: doc.id, ...doc.data() });
  });
  console.log(`Found ${recognitions.length} total recognitions\n`);

  // Identify issues
  const toFixNames = [];
  const toDelete = [];

  for (const rec of recognitions) {
    const student = studentsMap.get(rec.studentId);

    // Check if student exists at all
    if (!student) {
      toDelete.push({
        id: rec.id,
        studentName: rec.studentName,
        reason: rec.reason,
        issue: 'student_not_found'
      });
      continue;
    }

    // Check for past students - by status field OR boolean flags
    const isPastStudent =
      student.status === 'graduated' ||
      student.status === 'inactive' ||
      student.graduated === true ||
      student.active === false;

    if (isPastStudent) {
      toDelete.push({
        id: rec.id,
        studentName: rec.studentName,
        reason: rec.reason,
        issue: `past_student (status: ${student.status || 'n/a'}, graduated: ${student.graduated}, active: ${student.active})`
      });
      continue;
    }

    // Check for "undefined" in name - fix if student exists and is active
    if (rec.studentName?.includes('undefined')) {
      const lastName = student.lastName || student.surname || '';
      const correctName = lastName ? `${student.firstName} ${lastName}` : student.firstName;

      toFixNames.push({
        id: rec.id,
        currentName: rec.studentName,
        correctName,
        reason: rec.reason
      });
    }
  }

  // Report findings
  console.log('-'.repeat(50));
  console.log('ISSUES FOUND:');
  console.log(`  Names to fix: ${toFixNames.length}`);
  console.log(`  To delete (graduated/missing): ${toDelete.length}`);
  console.log('');

  if (toFixNames.length > 0) {
    console.log('Names to fix:');
    toFixNames.slice(0, 10).forEach(item => {
      console.log(`  "${item.currentName}" → "${item.correctName}"`);
    });
    if (toFixNames.length > 10) {
      console.log(`  ... and ${toFixNames.length - 10} more`);
    }
    console.log('');
  }

  if (toDelete.length > 0) {
    console.log('To delete:');
    toDelete.slice(0, 10).forEach(item => {
      console.log(`  ${item.studentName} (${item.issue})`);
    });
    if (toDelete.length > 10) {
      console.log(`  ... and ${toDelete.length - 10} more`);
    }
    console.log('');
  }

  const results = {
    namesFixed: 0,
    deleted: 0,
    errors: []
  };

  if (execute) {
    console.log('-'.repeat(50));
    console.log('APPLYING FIXES...\n');

    // Fix names
    for (const item of toFixNames) {
      try {
        const docRef = doc(db, 'schools', schoolId, 'recognitions', item.id);
        await updateDoc(docRef, {
          studentName: item.correctName,
          updatedAt: serverTimestamp(),
          _fixedAt: serverTimestamp(),
          _fixedFrom: item.currentName
        });
        results.namesFixed++;
      } catch (error) {
        console.error(`Error fixing ${item.currentName}:`, error.message);
        results.errors.push({ id: item.id, error: error.message });
      }
    }

    // Delete graduated/missing
    for (const item of toDelete) {
      try {
        const docRef = doc(db, 'schools', schoolId, 'recognitions', item.id);
        await deleteDoc(docRef);
        results.deleted++;
      } catch (error) {
        console.error(`Error deleting ${item.studentName}:`, error.message);
        results.errors.push({ id: item.id, error: error.message });
      }
    }

    console.log('');
  }

  // Summary
  console.log('='.repeat(60));
  console.log('SUMMARY');
  console.log('='.repeat(60));

  if (execute) {
    console.log(`Names fixed: ${results.namesFixed}`);
    console.log(`Deleted: ${results.deleted}`);
    if (results.errors.length > 0) {
      console.log(`Errors: ${results.errors.length}`);
    }
    console.log('\nDone! Clear cache to see updated data:');
    console.log('  window.quickLoadCache.clear("swan_recognitions_' + schoolId + '")');
  } else {
    console.log(`Would fix: ${toFixNames.length} names`);
    console.log(`Would delete: ${toDelete.length} recognitions`);
    console.log('\n*** PREVIEW ONLY - No changes made ***');
    console.log('To apply fixes, run:');
    console.log('  await window.fixCleanWeekRecognitions({ execute: true })');
  }

  return {
    success: true,
    toFixNames: toFixNames.length,
    toDelete: toDelete.length,
    namesFixed: results.namesFixed,
    deleted: results.deleted,
    errors: results.errors
  };
}

export { fixCleanWeekRecognitions };
